import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";
import { Loader2, Star, Check, X } from "lucide-react";

export default function ReviewDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setR(null); setErr(false);
    api.get(`/reviews/${slug}`).then((res) => setR(res.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-md mx-auto py-20 text-center" data-testid="review-not-found">
    <div className="font-display text-3xl">Review not found</div>
    <Link to="/reviews" className="text-blue-600 mt-4 inline-block">← All reviews</Link>
  </div>;
  if (!r) return <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin"/></div>;

  return (
    <article className="max-w-4xl mx-auto px-6 lg:px-10 py-12" data-testid={`review-page-${r.slug}`}>
      <SEO
        title={r.meta_title || r.title}
        description={r.meta_description || r.verdict}
        image={r.og_image || r.hero_image}
      />
      <Link to="/reviews" className="text-sm text-blue-600 hover:underline">← All reviews</Link>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-6">Review</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">{r.title}</h1>

      <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1 text-amber-500">
          <Star className="w-4 h-4 fill-current"/> <span className="font-mono font-semibold text-[#1F2937]">{Number(r.expert_rating).toFixed(1)}</span>
          <span className="text-muted-foreground">/ 10</span>
        </div>
        {r.author && <span>· {r.author}</span>}
        {r.published_at && <span>· {new Date(r.published_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>}
      </div>

      {r.hero_image && (
        <div className="mt-8 aspect-[16/9] rounded-3xl overflow-hidden bg-[#F3F6FA] border border-[#E5E7EB]">
          <SmartImage src={r.hero_image} alt={r.title} label={r.title}/>
        </div>
      )}

      <div className="mt-8 rounded-3xl border border-[#E5E7EB] bg-white p-6" data-testid="review-verdict">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Verdict</div>
        <p className="mt-2 text-lg leading-relaxed">{r.verdict}</p>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="review-pros">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Pros</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(r.pros || []).map((p, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>{p}</li>)}
          </ul>
        </div>
        <div className="rounded-3xl border border-rose-200 bg-rose-50/50 p-5" data-testid="review-cons">
          <div className="text-xs uppercase tracking-[0.2em] text-rose-700 font-semibold">Cons</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(r.cons || []).map((c, i) => <li key={i} className="flex gap-2"><X className="w-4 h-4 text-rose-600 shrink-0 mt-0.5"/>{c}</li>)}
          </ul>
        </div>
      </div>

      {r.body && (
        <div className="prose max-w-none mt-10" dangerouslySetInnerHTML={{ __html: r.body }} data-testid="review-body"/>
      )}

      {r.car_slug && (
        <div className="mt-10 rounded-3xl border border-[#E5E7EB] bg-[#F8FAFC] p-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ready to buy?</div>
          <Link to={`/cars/${r.car_slug}`} data-testid="review-cta-car"
            className="btn-primary h-11 px-6 mt-3 inline-flex items-center gap-2 text-sm">
            See full details of this car →
          </Link>
        </div>
      )}
    </article>
  );
}
